import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Package,
  Truck,
  ShoppingCart,
  Tag,
  Check,
  Loader2,
  Calendar,
  Bell,
  RefreshCw,
  Settings,
  CheckCircle2,
  Info,
  AlertTriangle,
  Flower2,
  RotateCcw,
  MessageCircle,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';

interface CiceksepetiIntegration {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  descriptionEn: string;
  tooltip: string;
  tooltipEn: string;
  icon: typeof Package;
  enabled: boolean;
  status: 'active' | 'inactive' | 'error' | 'rate_limited';
  hasRateLimit?: boolean;
  rateLimitNote?: string;
  rateLimitNoteEn?: string;
}

interface CiceksepetiSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectionId: string;
  onSave: (settings: CiceksepetiSettings) => void;
}

export interface CiceksepetiSettings {
  syncFrequency: number;
  notifications: boolean;
  integrations: Record<string, boolean>;
}

const defaultIntegrations: CiceksepetiIntegration[] = [
  {
    id: 'products',
    name: 'Urun Listeleme',
    nameEn: 'Product Listings',
    description: 'Urunlerinizi, stok ve fiyat bilgilerini goruntuluyin',
    descriptionEn: 'View your products, stock and price information',
    tooltip: 'Tum urunlerinizi, stok durumlarini, fiyatlarini ve listeleme durumlarini takip edin. Urun bazli analizler yapin.',
    tooltipEn: 'Track all your products, stock levels, prices and listing status. Perform product-based analysis.',
    icon: Package,
    enabled: true,
    status: 'active',
    hasRateLimit: true,
    rateLimitNote: 'Bu endpoint 10 dakikada 1 kez sorgulanabilir.',
    rateLimitNoteEn: 'This endpoint can be queried once every 10 minutes.',
  },
  {
    id: 'orders',
    name: 'Siparis Entegrasyonu',
    nameEn: 'Order Integration',
    description: 'Siparisleri otomatik olarak cek ve analiz et',
    descriptionEn: 'Automatically fetch and analyze orders',
    tooltip: 'Tum siparis statuslerini takip edin. Siparis trendlerini, ortalama sepet tutarini ve en cok satan urunleri analiz edin.',
    tooltipEn: 'Track all order statuses. Analyze order trends, average basket value and best-selling products.',
    icon: ShoppingCart,
    enabled: true,
    status: 'active',
  },
  {
    id: 'categories',
    name: 'Kategori Bilgileri',
    nameEn: 'Category Information',
    description: 'Ciceksepeti kategori agacini ve bilgilerini goruntuluyin',
    descriptionEn: 'View Ciceksepeti category tree and information',
    tooltip: 'Tum kategorileri, alt kategorileri ve kategori ozelliklerini goruntuluyin. Urunlerinizi dogru kategorilere yerlestirin.',
    tooltipEn: 'View all categories, subcategories and category attributes. Place your products in the correct categories.',
    icon: Tag,
    enabled: true,
    status: 'active',
  },
  {
    id: 'shipping',
    name: 'Kargo Yonetimi',
    nameEn: 'Shipping Management',
    description: 'Kargo durumlarini ve teslimat surelerini takip edin',
    descriptionEn: 'Track shipping status and delivery times',
    tooltip: 'Gonderilen paketlerin durumlarini, kargo firmasi bilgilerini ve teslimat surelerini izleyin.',
    tooltipEn: 'Monitor package status, carrier information and delivery times.',
    icon: Truck,
    enabled: true,
    status: 'active',
  },
  {
    id: 'returns',
    name: 'Iade Yonetimi',
    nameEn: 'Returns Management',
    description: 'Iade siparislerini ve sebeplerini analiz edin',
    descriptionEn: 'Analyze return orders and reasons',
    tooltip: 'Iade taleplerini, iade sebeplerini ve iptal edilen siparisleri takip edin. En cok iade edilen urunleri analiz edin.',
    tooltipEn: 'Track return requests, reasons and canceled orders. Analyze most returned products.',
    icon: RotateCcw,
    enabled: true,
    status: 'active',
    hasRateLimit: true,
    rateLimitNote: 'Bu endpoint 2 dakikada 1 kez sorgulanabilir.',
    rateLimitNoteEn: 'This endpoint can be queried once every 2 minutes.',
  },
  {
    id: 'questions',
    name: 'Soru-Cevap Yonetimi',
    nameEn: 'Q&A Management',
    description: 'Musteri sorularini takip edin ve analiz edin',
    descriptionEn: 'Track and analyze customer questions',
    tooltip: 'Musterilerin urunleriniz hakkinda sordugu sorulari goruntuluyin. Musteri etkilesim analizleri yapin.',
    tooltipEn: 'View customer questions about your products. Perform customer engagement analysis.',
    icon: MessageCircle,
    enabled: true,
    status: 'active',
    hasRateLimit: true,
    rateLimitNote: 'Bu endpoint 3 dakikada 1 kez sorgulanabilir.',
    rateLimitNoteEn: 'This endpoint can be queried once every 3 minutes.',
  },
];

const syncFrequencyOptions = [
  { value: 900, label: '15 Dakika', labelEn: '15 Minutes' },
  { value: 1800, label: '30 Dakika', labelEn: '30 Minutes' },
  { value: 3600, label: 'Saatlik', labelEn: 'Hourly' },
  { value: 21600, label: '6 Saatte Bir', labelEn: 'Every 6 Hours' },
  { value: 43200, label: '12 Saatte Bir', labelEn: 'Every 12 Hours' },
  { value: 86400, label: 'Gunluk', labelEn: 'Daily' },
];

export function CiceksepetiSettingsModal({
  isOpen,
  onClose,
  connectionId,
  onSave,
}: CiceksepetiSettingsModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();

  const [integrations, setIntegrations] = useState<CiceksepetiIntegration[]>(defaultIntegrations);
  const [syncFrequency, setSyncFrequency] = useState(3600);
  const [notifications, setNotifications] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'integrations' | 'sync' | 'advanced'>('integrations');

  // Load saved settings on mount
  useEffect(() => {
    if (isOpen && user?.uid && connectionId) {
      loadSettings();
    }
  }, [isOpen, user?.uid, connectionId]);

  const loadSettings = async () => {
    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(
        `${FUNCTIONS_URL}/getCiceksepetiSettings?userId=${user?.uid}&connectionId=${connectionId}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setSyncFrequency(data.settings.syncFrequency || 3600);
          setNotifications(data.settings.notifications ?? true);

          if (data.settings.integrations) {
            setIntegrations(prev => prev.map(int => ({
              ...int,
              enabled: data.settings.integrations[int.id] ?? true,
              status: data.settings.integrations[int.id] ? 'active' : 'inactive',
            })));
          }
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const toggleIntegration = (id: string) => {
    setIntegrations(prev =>
      prev.map(integration =>
        integration.id === id
          ? {
              ...integration,
              enabled: !integration.enabled,
              status: !integration.enabled ? 'active' : 'inactive',
            }
          : integration
      )
    );
  };

  const handleSave = async () => {
    if (!user?.uid) return;

    setIsSaving(true);

    const settings: CiceksepetiSettings = {
      syncFrequency,
      notifications,
      integrations: integrations.reduce((acc, int) => {
        acc[int.id] = int.enabled;
        return acc;
      }, {} as Record<string, boolean>),
    };

    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(`${FUNCTIONS_URL}/saveCiceksepetiSettings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          connectionId,
          settings,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      onSave(settings);
      onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSyncNow = async () => {
    if (!user?.uid) return;

    setIsSyncing(true);

    try {
      // Trigger sync for all enabled integrations
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

      const enabledIntegrations = integrations.filter(i => i.enabled);

      // Sync each enabled integration
      for (const integration of enabledIntegrations) {
        const endpoint = getEndpointForIntegration(integration.id);
        if (endpoint) {
          await fetch(`${FUNCTIONS_URL}/${endpoint}?userId=${user.uid}&connectionId=${connectionId}`);
        }
      }

      // Small delay for UX
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const getEndpointForIntegration = (id: string): string | null => {
    const endpoints: Record<string, string> = {
      products: 'getCiceksepetiListings',
      orders: 'getCiceksepetiOrders',
      categories: 'getCiceksepetiCategories',
      returns: 'getCiceksepetiReturns',
      questions: 'getCiceksepetiQuestions',
    };
    return endpoints[id] || null;
  };

  const enabledCount = integrations.filter(i => i.enabled).length;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border-default flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border-default bg-gradient-to-r from-[#7B2D8E]/10 to-transparent">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#7B2D8E] flex items-center justify-center shadow-lg">
              <Flower2 size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">
                Ciceksepeti {isEnglish ? 'Settings' : 'Ayarlari'}
              </h2>
              <p className="text-sm text-text-secondary">
                {enabledCount} {isEnglish ? 'active integrations' : 'aktif entegrasyon'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-bg-elevated transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-default">
          {[
            { id: 'integrations', label: isEnglish ? 'Integrations' : 'Entegrasyonlar', icon: Package },
            { id: 'sync', label: isEnglish ? 'Sync Settings' : 'Senkronizasyon', icon: RefreshCw },
            { id: 'advanced', label: isEnglish ? 'Advanced' : 'Gelismis', icon: Settings },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={cn(
                'flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px',
                activeTab === tab.id
                  ? 'border-[#7B2D8E] text-[#7B2D8E]'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              )}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'integrations' && (
            <div className="space-y-3">
              <p className="text-sm text-text-secondary mb-4">
                {isEnglish
                  ? 'Enable or disable integrations to control what data is synced with Ciceksepeti.'
                  : 'Ciceksepeti ile senkronize edilecek verileri kontrol etmek icin entegrasyonlari acin veya kapatin.'}
              </p>

              {integrations.map(integration => {
                const Icon = integration.icon;
                return (
                  <div
                    key={integration.id}
                    className={cn(
                      'flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer',
                      integration.enabled
                        ? 'border-[#7B2D8E]/30 bg-[#7B2D8E]/5 hover:border-[#7B2D8E]/50'
                        : 'border-border-default hover:border-border-hover bg-bg-elevated/50'
                    )}
                    onClick={() => toggleIntegration(integration.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={cn(
                          'w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
                          integration.enabled
                            ? 'bg-[#7B2D8E]/20 text-[#7B2D8E]'
                            : 'bg-bg-elevated text-text-tertiary'
                        )}
                      >
                        <Icon size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-text-primary">
                            {isEnglish ? integration.nameEn : integration.name}
                          </h4>
                          {integration.hasRateLimit && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-500 border border-amber-500/30">
                              Rate Limit
                            </span>
                          )}
                          <div className="group relative">
                            <Info
                              size={14}
                              className="text-text-tertiary hover:text-[#7B2D8E] cursor-help transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 rounded-lg bg-bg-surface border border-border-default shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                              <p className="text-xs text-text-secondary leading-relaxed">
                                {isEnglish ? integration.tooltipEn : integration.tooltip}
                              </p>
                              {integration.hasRateLimit && (
                                <p className="text-xs text-amber-500 mt-2 pt-2 border-t border-border-default">
                                  {isEnglish ? integration.rateLimitNoteEn : integration.rateLimitNote}
                                </p>
                              )}
                              <div className="absolute left-3 bottom-0 translate-y-1/2 rotate-45 w-2 h-2 bg-bg-surface border-r border-b border-border-default"></div>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-text-tertiary mt-0.5">
                          {isEnglish ? integration.descriptionEn : integration.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {integration.enabled && (
                        <span className="flex items-center gap-1 text-xs text-green-500">
                          <CheckCircle2 size={14} />
                          {isEnglish ? 'Active' : 'Aktif'}
                        </span>
                      )}
                      <div
                        className={cn(
                          'w-11 h-6 rounded-full relative transition-colors',
                          integration.enabled ? 'bg-[#7B2D8E]' : 'bg-bg-elevated border border-border-default'
                        )}
                      >
                        <div
                          className={cn(
                            'w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform shadow-sm',
                            integration.enabled ? 'translate-x-5' : 'translate-x-0.5'
                          )}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'sync' && (
            <div className="space-y-6">
              {/* Sync Frequency */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                  <Calendar size={16} className="text-[#7B2D8E]" />
                  {isEnglish ? 'Sync Frequency' : 'Senkronizasyon Sikligi'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {syncFrequencyOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setSyncFrequency(option.value)}
                      className={cn(
                        'px-3 py-2.5 rounded-lg text-sm font-medium transition-all border',
                        syncFrequency === option.value
                          ? 'border-[#7B2D8E] bg-[#7B2D8E]/10 text-[#7B2D8E]'
                          : 'border-border-default text-text-secondary hover:border-[#7B2D8E]/50'
                      )}
                    >
                      {isEnglish ? option.labelEn : option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rate Limit Warning */}
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-text-primary text-sm">
                      {isEnglish ? 'Rate Limit Notice' : 'Rate Limit Bildirimi'}
                    </h4>
                    <p className="text-xs text-text-secondary mt-1">
                      {isEnglish
                        ? 'Ciceksepeti API has rate limits. Product listings can only be queried once every 10 minutes. Other endpoints may also have restrictions.'
                        : 'Ciceksepeti API rate limit uygulamaktadir. Urun listeleme 10 dakikada 1 kez sorgulanabilir. Diger endpointlerde de kisitlamalar olabilir.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notifications */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                  <Bell size={16} className="text-[#7B2D8E]" />
                  {isEnglish ? 'Notifications' : 'Bildirimler'}
                </label>
                <div className="space-y-2">
                  <label
                    className="flex items-center justify-between p-3 rounded-lg border border-border-default cursor-pointer hover:border-[#7B2D8E]/50 transition-all"
                    onClick={() => setNotifications(!notifications)}
                  >
                    <span className="text-sm text-text-secondary">
                      {isEnglish ? 'Notify me when sync completes' : 'Senkronizasyon tamamlandiginda bildir'}
                    </span>
                    <div
                      className={cn(
                        'w-11 h-6 rounded-full relative cursor-pointer transition-colors',
                        notifications ? 'bg-[#7B2D8E]' : 'bg-bg-elevated'
                      )}
                    >
                      <div
                        className={cn(
                          'w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform',
                          notifications ? 'translate-x-5' : 'translate-x-0.5'
                        )}
                      />
                    </div>
                  </label>
                </div>
              </div>

              {/* Manual Sync Button */}
              <div className="pt-4 border-t border-border-default">
                <button
                  onClick={handleSyncNow}
                  disabled={isSyncing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-[#7B2D8E] text-[#7B2D8E] hover:bg-[#7B2D8E]/10 transition-colors font-medium disabled:opacity-50"
                >
                  {isSyncing ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {isEnglish ? 'Syncing...' : 'Senkronize Ediliyor...'}
                    </>
                  ) : (
                    <>
                      <RefreshCw size={18} />
                      {isEnglish ? 'Sync Now' : 'Simdi Senkronize Et'}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="space-y-4">
              {/* API Info */}
              <div className="p-4 rounded-xl bg-[#7B2D8E]/5 border border-[#7B2D8E]/20">
                <div className="flex items-start gap-3">
                  <Info size={20} className="text-[#7B2D8E] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-text-primary text-sm">
                      {isEnglish ? 'API Endpoint' : 'API Endpoint'}
                    </h4>
                    <p className="text-xs text-text-secondary mt-1">
                      {isEnglish
                        ? 'This connection uses the Ciceksepeti API:'
                        : 'Bu baglanti Ciceksepeti API\'sini kullanir:'}
                    </p>
                    <ul className="text-xs text-text-tertiary mt-2 space-y-1">
                      <li>• apis.ciceksepeti.com/api/v1</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Available Endpoints */}
              <div className="p-4 rounded-xl bg-bg-elevated border border-border-default">
                <h4 className="font-medium text-text-primary text-sm mb-3">
                  {isEnglish ? 'Available Endpoints' : 'Kullanilabilir Endpointler'}
                </h4>
                <ul className="text-xs text-text-tertiary space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={12} className="text-green-500" />
                    <span>/Products - {isEnglish ? 'Product listings' : 'Urun listeleme'}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={12} className="text-green-500" />
                    <span>/Categories - {isEnglish ? 'Category information' : 'Kategori bilgileri'}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={12} className="text-green-500" />
                    <span>/Order/GetOrders - {isEnglish ? 'Order list' : 'Siparis listesi'}</span>
                  </li>
                </ul>
              </div>

              {/* Danger Zone */}
              <div className="pt-4 border-t border-border-default">
                <h4 className="text-sm font-medium text-error mb-3">
                  {isEnglish ? 'Danger Zone' : 'Tehlikeli Bolge'}
                </h4>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-error text-error hover:bg-error/10 transition-colors font-medium">
                  {isEnglish ? 'Reset All Settings' : 'Tum Ayarlari Sifirla'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-border-default bg-bg-elevated/50">
          <p className="text-xs text-text-tertiary">
            {enabledCount} / {integrations.length} {isEnglish ? 'integrations enabled' : 'entegrasyon aktif'}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
            >
              {isEnglish ? 'Cancel' : 'Iptal'}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-[#7B2D8E] text-white hover:bg-[#6B2580] transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {isEnglish ? 'Saving...' : 'Kaydediliyor...'}
                </>
              ) : (
                <>
                  <Check size={16} />
                  {isEnglish ? 'Save Settings' : 'Ayarlari Kaydet'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
